import { useEffect, useRef, useState } from 'react';
import { Grid, Button, Dialog, DialogContent, DialogActions, Toolbar, Accordion, AccordionSummary, AccordionDetails, Card } from '@mui/material';
import EnhancedTable from './table';
import EventTable from './event_table';
import Config from '../config';
import DocLink from './document_link';
import Moment from 'react-moment';
import DownloadButton from './download_button';
import DocType from './document_type';
import Status from './status';
import { documentSignalrUpdate, parseSignalrMessage } from '../helpers';
import Auth from '../auth';
import { SimpleRte } from './EnhancedRte';
import { Timeline } from 'react-event-timeline';
import { ExpandMore } from '@mui/icons-material';
import { Post, writeStatuses } from '../pages/messages/thread';
import ExtendedSelect from './select';
import axios from 'axios';

const writeStatusOptions = writeStatuses.map((r: any) => ({value: r, label: r}));

interface DocumentTablesProps {
    metadata: any;
    signalr?: any;
    refresh?: any;
    showPosts?: boolean;
    onPostLoad?: (posts: any[]) => void;
    onEventLoad?: (...args: any[]) => void;
    onEdiExists?: (value: boolean) => void;
    [key: string]: any;
}

const DocumentTables = (props: DocumentTablesProps) => {
    const { metadata, signalr, refresh, showPosts, onPostLoad, onEventLoad, onEdiExists } = props;

    // eventId (initial null) is preserved from the original state shape but is
    // never read or written anywhere in the component.
    useState<any>(null);
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [posts, setPosts] = useState<any[]>([]);
    const [refreshEvent, setRefreshEvent] = useState<boolean | undefined>(undefined);
    const [refreshAttachments, setRefreshAttachments] = useState<boolean | undefined>(undefined);
    const [refreshHistory, setRefreshHistory] = useState<boolean | undefined>(undefined);
    const [refreshRelated] = useState<boolean | undefined>(undefined);
    const [disableReply, setDisableReply] = useState<boolean | undefined>(undefined);
    const [reply, setReply] = useState<string | undefined>(undefined);
    const [replyEmpty, setReplyEmpty] = useState<boolean | undefined>(undefined);
    const [replyToPost, setReplyToPost] = useState<any>(undefined);
    const [replyToThread, setReplyToThread] = useState<any>(undefined);
    const [postStatus, setPostStatus] = useState<string | undefined>(undefined);

    // Track the active document id synchronously so an in-flight refresh can
    // detect when the user navigated to a different document and avoid
    // overwriting the new document's posts with the previous one's results.
    const latestMetadataIdRef = useRef((metadata || {}).Id);
    latestMetadataIdRef.current = (metadata || {}).Id;

    const refreshPosts = async () => {
        if(!showPosts){
            setPosts([]);
            return;
        }
        let loaded: any[] = [];
        if(metadata && metadata.Id){
            const requestedId = metadata.Id;
            loaded = (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$filter=LinkKey eq '${metadata.LinkKey}'&$orderby=Date desc`)).data.value;
            // If the active document changed while awaiting, discard this stale
            // result so a prior document's refresh cannot clobber the current one.
            if(latestMetadataIdRef.current !== requestedId){
                return;
            }
        }
        setPosts(loaded);
        if(onPostLoad){
            onPostLoad(loaded);
        }
    };

    const handleReply = (postId?: any, threadId?: any) => {
        setShowMessage(true);
        setReply('');
        setReplyEmpty(true);
        setReplyToPost(postId);
        setReplyToThread(threadId);
        setPostStatus('');
    };

    const sendPost = async () => {
        let coid = parseInt(Auth.getAccountNumber());
        let partner = metadata.PartnerCoId;
        if(partner === coid){
            partner = metadata.CoId;
        }
        setDisableReply(true);
        let html = reply;
        try{
            let post: any = posts.filter(r => r.Sender !== coid)[0];
            if(!post){
                post = posts[0];
            }
            if(post || replyToPost){
                let threadid = replyToThread || post.ThreadId || post.Id;
                await axios.post(Config.api + '/odata/Company/Functions.NewPost', {
                    ReplyTo: replyToPost || post.Id,
                    ThreadId: threadid,
                    Body: html,
                    DocId: metadata.Id
                });
            } else {
                await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
                    Body: html,
                    Subject: metadata.PartnerPo,
                    DocId: metadata.Id,
                    Recipients: [partner],
                    Status: postStatus
                });
            }

            setShowMessage(false);
            setDisableReply(false);
            refreshPosts();
        } catch(e) {
            setDisableReply(false);
        }
    };

    // Mirror posts synchronously so the signalr handler always builds on the
    // latest array. React 18 auto-batches consecutive notifications in one tick;
    // reading a captured `posts` would let the second message overwrite the first.
    const postsRef = useRef<any[]>(posts);
    postsRef.current = posts;

    const postSignalrHandler = (message: any) => {
        if(!showPosts){
            return;
        }
        message = parseSignalrMessage(message);
        if(message && message.action === 'newThreadMessage' && message.additionalData && message.additionalData.LinkKey === metadata.LinkKey){
            const current = postsRef.current;
            if(current.filter(r => r.Id === message.additionalData.Id).length > 0){
                return;
            }
            const nextPosts = [message.additionalData, ...current];
            postsRef.current = nextPosts;
            setPosts(nextPosts);
            if(onPostLoad){
                onPostLoad(nextPosts);
            }
        }
    };

    // Keep the signalr handler reading the latest props/state, mirroring the
    // stable instance method on the original class component.
    const postSignalrHandlerRef = useRef(postSignalrHandler);
    postSignalrHandlerRef.current = postSignalrHandler;

    const onAttachmentLoad = (data: any) => {
        if(data && data.length && data.find((r: any) => r.Type === "edi" && r.Description && r.Description.indexOf("EDI") === 0) && onEdiExists){
            onEdiExists(true);
        }
    };

    // componentDidMount: initial post load.
    useEffect(() => {
        refreshPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidMount + componentWillUnmount: register/unregister the signalr
    // notify handler whenever a signalr connection is present.
    useEffect(() => {
        if(!signalr){
            return;
        }
        const listener = (message: any) => postSignalrHandlerRef.current(message);
        signalr.on('notify', listener);
        return () => {
            signalr.off('notify', listener);
        };
    }, [signalr]);

    // componentDidUpdate: react to refresh / metadata Id changes.
    const didMountRef = useRef(false);
    const prevRefreshRef = useRef(refresh);
    const prevMetadataIdRef = useRef((metadata || {}).Id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if(!didMountRef.current){
            didMountRef.current = true;
            prevRefreshRef.current = refresh;
            prevMetadataIdRef.current = (metadata || {}).Id;
            return;
        }
        if(prevRefreshRef.current !== refresh){
            setRefreshEvent(v => !v);
            setRefreshAttachments(v => !v);
            setRefreshHistory(v => !v);
            refreshPosts();
        } else if((metadata || {}).Id !== prevMetadataIdRef.current){
            refreshPosts();
        }
        prevRefreshRef.current = refresh;
        prevMetadataIdRef.current = (metadata || {}).Id;
    });

    let coid = parseInt(Auth.getAccountNumber());
    let eventFilter = {filters: [{field: "CustomerViewable", operator: 'eq', value: true}, {field: "DocId", operator: 'eq', value: metadata.Id}]};
    let attachmentConfig = {
        url: Config.api + "/odata/Company/Attachments",
        columns: [
          { id: "Id", label: 'ID#'},
          { id: 'Type', filterable: true, label: 'Type', width: '12em' },
          { id: 'Description', filterable: true, label: 'Description', allowStringOperators: true },
          { id: 'CreateDate', type:'date', filterable: true, sortable: true, label: 'Date', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
          { id: 'BlobPath', width: '10em', template: (value, row) => {return (<DownloadButton variant='outlined' href={value}>Download</DownloadButton>);}}
      ],
      refresh: refreshAttachments,
      order: 'desc',
      orderBy: 'Id',
      keyField: 'Id',
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      filter: {filters: [{field: "DocId", operator: 'eq', value: metadata.Id}]},
      title: 'Attachments',
      onLoad: onAttachmentLoad
  };
    let relatedConfig = {
        url: Config.api + `/odata/Company/Functions.DocumentSearch`,
        columns: [
          { id: "id", hidden: true},
          { id: 'internalId', type: 'number', filterable: true, sortable: true, numeric: true, disablePadding: true, label: 'ID#', width: '10em' },
          { id: 'partnerPo', filterable: true, label: 'Reference Number', width: '12em' },
          { id: 'date', type:'date', filterable: true, sortable: true, numeric: false, disablePadding: false, label: 'Date', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
          { id: 'documentType', filterable: true, numeric: false, disablePadding: false, label: 'Type', template: (value) => <DocType docType={value}/> },
          { id: 'statusDescription', filterable: true, numeric: false, disablePadding: false, label: 'Status', template: (value) => (<Status description={value}/>), width: '14em' },
          { id: 'source', filterable: true, label: 'System', width: '10em' },
          { command: 'commands', stopPropagation: 'true', width: '10em', template: (value, row) => (
              <Button size='small' variant='outlined' component={DocLink} docType={row.documentType} id={row.id}>View</Button>
          )}
      ],
      order: 'desc',
      orderBy: 'internalId',
      pageSize: 10,
      signalr: signalr,
      onSignalrUpdate: documentSignalrUpdate,
      signalrProps: {
          refreshIfNew: true,
          linkKey: metadata.LinkKey,
          id: metadata.Id
      },
      refresh: refreshRelated,
      pageSizes: [10, 20, 50, 100],
      filter: {filters: [{field: "internalId", operator: 'neq', value: metadata.Id}, {filters: [
          {field: "linkKey", operator: 'eq', value: metadata.LinkKey},
          {field: "sourceKey", operator: 'eq', value: metadata.SourceKey},
          {field: "partnerPo", operator: 'eq', value: metadata.SourceKey}
      ], logic: 'or'}], logic: 'and'},
      title: 'Related Documents'
    };
    let historyConfig = {
        url: Config.api + `/odata/Company/WorkflowHistory`,
        columns: [
          { id: 'WorkflowDescription', label: 'Action' },
          { id: 'WorkflowStatusCode', hidden: true },
          { id: 'FirstRunTime', type:'date', sortable: true, label: 'First Run', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
          { id: 'LastRunTime', type:'date', sortable: true, label: 'Last Run', template: (value) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
          { id: 'WorkflowStatus', label: 'Run On Status', template: (value, row) => (<Status description={value || row.WorkflowStatusCode}/>), width: '15em' },
          { id: 'RunCount', label: 'Attempts', width: '10em' }
      ],
      keyField: 'FirstRunTime',
      order: 'desc',
      orderBy: 'LastRunTime',
      pageSize: 10,
      refresh: refreshHistory,
      pageSizes: [10, 20, 50, 100],
      filter: {filters: [{field: "Id", operator: 'eq', value: metadata.Id}]},
      title: 'Document Processing History'
    };
    return (<Grid container spacing={2}><Grid className='noPrint' size={12}>
      <EventTable title="Events" filter={eventFilter} refresh={refreshEvent} collapsible={true} collapsed onLoad={onEventLoad}/>
    </Grid>
    {showPosts && <Grid className='noPrint' size={12}>
    <Card>
        <Accordion>
            <AccordionSummary id="postPanel" expandIcon={<ExpandMore />}>
              <Toolbar className='lbtoolbar'>Messages</Toolbar>
            </AccordionSummary>
            <AccordionDetails style={{flexDirection: 'column', textAlign: 'left'}}>
            <div>
                <Button color='primary' variant='contained' onClick={() => handleReply()}>New Message</Button>
            </div>
            {posts.length > 0 && <Timeline style={{fontSize: '100%'}}>
            {posts.map(post =>
                <Post key={post.Id} coid={coid} post={post} reply={handleReply} contentStyle={{boxShadow: 'none', paddingLeft: 0}}/>
            )}
            </Timeline>}
            </AccordionDetails>
          </Accordion>
      </Card>

    </Grid>}
    <Grid className='noPrint' size={12}>
      <EnhancedTable config={attachmentConfig} collapsible={true} collapsed/>
    </Grid>
    <Grid className='noPrint' size={12}>
      <EnhancedTable config={relatedConfig} collapsible={true} collapsed/>
    </Grid>
    <Grid className='noPrint' size={12}>
      <EnhancedTable config={historyConfig} collapsible={true} collapsed/>
    </Grid>
    <Dialog maxWidth='md' fullWidth open={showMessage} onClose={() => setShowMessage(false)}>
        <Toolbar className='lbtoolbar'>New Message</Toolbar>
        <DialogContent style={{height: '300px', display: 'flex', flexDirection: 'column'}}>
        {(posts && posts.length === 0) && <div style={{display: 'flex', flexDirection: 'row', marginBottom: '1em'}}>
            <ExtendedSelect disabled={disableReply} style={{flex: 1, alignSelf: 'flex-end'}} variant='outlined' placeholder='Status' value={postStatus} options={writeStatusOptions} onChange={(e) => setPostStatus(e)}/>
        </div>}
        <SimpleRte className='textEditor' value={reply} disabled={disableReply} onChange={(html, isEmpty) => { setReply(html); setReplyEmpty(isEmpty); }}/>
        </DialogContent>
        <DialogActions>
        <Button onClick={sendPost} disabled={replyEmpty || disableReply} color="primary">
          Send
        </Button>
      </DialogActions>
    </Dialog>
    </Grid>);
};

export default DocumentTables;
